import { getAuthToken } from '../utils/authUtils'; //
const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';

/**
 * Service for managing patient portal functionality
 * Coordinates between Firebase Auth and backend API operations
 */

class PatientPortalService {
  /**
   * Enable patient portal access for a patient
   * @param {string} patientId - Patient's Firestore document ID
   * @param {Object} patientData - Patient's basic information
   * @returns {Promise<Object>} - Operation result
   */
  async enablePatientPortal(patientId, patientData, customPassword = null) {
    try {
      // Check if patient has an email address
      if (!patientData.email) {
        throw new Error('Patient must have an email address to enable patient portal access.');
      }

      // Check if patient portal user already exists
      const userExists = await this.checkPatientPortalUserExists(patientData.email);
      if (userExists) {
        throw new Error('A patient portal user already exists for this email address.');
      }

      // Generate a secure password if none provided
      if (!customPassword) {
        customPassword = this.generateSecurePassword();
      }

      // Use the new atomic endpoint that creates everything in one operation
      const result = await this.enablePatientPortalAtomic(
        patientId,
        patientData.email,
        customPassword
      );

      return {
        success: true,
        user: result.user,
        password: customPassword,
        message: 'Patient portal enabled successfully'
      };

    } catch (error) {
      console.error('Error enabling patient portal:', error);
      throw error;
    }
  }

  /**
   * Enable patient portal access using atomic backend endpoint
   * @param {string} patientId - Patient's Firestore document ID
   * @param {string} email - Patient's email address
   * @param {string} password - Initial password
   * @returns {Promise<Object>} - Operation result
   */
  async enablePatientPortalAtomic(patientId, email, password) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/enable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patientId,
          email,
          password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to enable patient portal');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error enabling patient portal atomically:', error);
      throw error;
    }
  }

  /**
   * Disable patient portal access
   * @param {string} patientId - Patient's Firestore document ID
   * @param {string} firebaseUid - Patient's Firebase Auth UID
   * @returns {Promise<Object>} - Operation result
   */
  async disablePatientPortal(patientId, firebaseUid) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/disable`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patientId,
          firebaseUid
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to disable patient portal');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error disabling patient portal:', error);
      throw error;
    }
  }

  /**
   * Create patient portal user profile
   * @param {string} firebaseUid - Firebase Auth user ID
   * @param {string} patientId - Patient's Firestore document ID
   * @param {Object} patientData - Patient's basic information
   * @returns {Promise<Object>} - Created profile data
   */
  async createPatientPortalProfile(firebaseUid, patientId, patientData) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/profile`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firebaseUid,
          patientId,
          patientData: {
            firstName: patientData.firstName || patientData.name?.split(' ')[0] || '',
            lastName: patientData.lastName || patientData.name?.split(' ').slice(1).join(' ') || '',
            dateOfBirth: patientData.dateOfBirth || '',
            phone: patientData.contact || patientData.phone || patientData.phoneNumber || '',
            facilityId: patientData.facilityId || ''
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create patient portal profile');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error creating patient portal profile:', error);
      throw error;
    }
  }

  /**
   * Update patient portal status
   * @param {string} patientId - Patient's Firestore document ID
   * @param {boolean} isEnabled - Whether the portal should be enabled
   * @param {string} firebaseUid - Patient's Firebase Auth UID (optional for disabling)
   * @returns {Promise<Object>} - Operation result
   */
  async updatePatientPortalStatus(patientId, isEnabled, firebaseUid = null) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/status`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patientId,
          isEnabled,
          firebaseUid
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update patient portal status');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error updating patient portal status:', error);
      throw error;
    }
  }

  /**
   * Update patient email address
   * @param {string} patientId - Patient's Firestore document ID
   * @param {string} newEmail - New email address
   * @returns {Promise<Object>} - Update result
   */
  async updatePatientEmail(patientId, newEmail) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/email`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patientId,
          newEmail
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update patient email');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error updating patient email:', error);
      throw error;
    }
  }

  /**
   * Update patient portal password
   * @param {string} patientId - Patient's Firestore document ID
   * @param {string} firebaseUid - Patient's Firebase Auth UID
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} - Update result
   */
  async updatePatientPassword(patientId, firebaseUid, newPassword) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/password`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patientId,
          firebaseUid,
          newPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update patient password');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error updating patient password:', error);
      throw error;
    }
  }

  /**
   * Check if a patient portal user exists
   * @param {string} email - Patient's email address
   * @returns {Promise<Object|null>} - User info if exists, null if not
   */
  async checkPatientPortalUserExists(email) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/check?email=${encodeURIComponent(email)}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        // If the endpoint doesn't exist yet, assume no user exists
        return null;
      }

      const result = await response.json();
      if (result.exists) {
        return {
          uid: result.uid,
          email: result.email,
          role: result.role
        };
      }
      return null;

    } catch (error) {
      console.error('Error checking if patient portal user exists:', error);
      // For now, return null to allow creation
      // In production, you might want to handle this differently
      return null;
    }
  }



  /**
   * Get patient portal user profile
   * @param {string} firebaseUid - Firebase Auth user ID
   * @returns {Promise<Object|null>} - User profile data
   */
  async getPatientPortalProfile(firebaseUid) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/profile/${firebaseUid}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get patient portal profile');
      }

      const result = await response.json();
      return result.profile;

    } catch (error) {
      console.error('Error getting patient portal profile:', error);
      return null;
    }
  }

  /**
   * Create Firebase Auth user through backend API
   * @param {string} patientId - Patient's Firestore document ID
   * @param {string} email - Patient's email address
   * @param {string} password - Initial password
   * @returns {Promise<Object>} - Created user data
   */
  async createFirebaseAuthUser(patientId, email, password) {
    try {
      const idToken = await getAuthToken();

      const response = await fetch(`${API_BASE}/admin/patient-portal/create-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          patientId,
          email,
          password
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create Firebase Auth user');
      }

      const result = await response.json();
      return result;

    } catch (error) {
      console.error('Error creating Firebase Auth user:', error);
      throw error;
    }
  }

  /**
   * Generate a secure password
   * @returns {string} - Generated password
   */
  generateSecurePassword() {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    return password;
  }
}

const patientPortalService = new PatientPortalService();
export default patientPortalService;
